<template>
    <div class="layout-font-settings w-full text-sm max-w-[468px]">
        <div class="w-full space-y-3 lg:space-y-6">
            <div class="w-full">
                <div class="font-semibold mb-1">
                    {{ $t('common.size') }}
                </div>
                <div class="">
                    {{ `${$t('common.recommended')}: ${settings.defaultSize} px` }}
                </div>
            </div>
            <div class="py-4 px-4 lg:px-6 rounded border border-ui-lines">
                <div class="mb-2">
                    {{ $t('layout.config.headerPreview') }}
                </div>
                <div
                    :style="{
                        fontSize: `${fontSizeModel}px`
                    }"
                    class="font-semibold leading-none"
                >
                    {{ $t('layout.config.headerPreviewText') }}
                </div>
            </div>
            <div class="">
                <el-slider
                    v-model="fontSizeModel"
                    :min="8"
                    :max="56"
                    :marks="sliderMarks"
                    :show-input-controls="false"
                    show-input
                />
            </div>
            <div
                v-if="!isSizeOnly"
                class="pt-6"
            >
                <div class="font-semibold mb-5">
                    {{ $t('common.chooseColor') }}
                </div>
                <div>
                    <LayoutSettingsColorPicker
                        v-model="colorFontModel"
                        :color-name="'Default'"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { LAYOUT_PARAMETERS_DEFAULTS } from '@/enum/default-layout-config'
import { computed } from 'vue'
import { Slider as ElSlider } from 'element-ui'
import LayoutSettingsColorPicker from '@/components/RedesignComponents/Layout/Common/LayoutSettingsColorPicker.vue'
import { ILayoutParametersList } from '@/types/dashboard'

interface PropSettingBase {
    key: string
    label: string
    fontParameterName: string
    defaultSize: number
}

interface PropsSizeOnlySettingType extends PropSettingBase {
    sizeOnly: true
}

interface PropSizeAndColorSettingType extends PropSettingBase {
    sizeOnly?: false | undefined
    colorParameterName: string
    defaultColor: string
}

export type PropsSettingType = PropsSizeOnlySettingType | PropSizeAndColorSettingType

const props = defineProps<{
    value: Array<ILayoutParametersList>
    settings: PropsSettingType
}>()

const emit = defineEmits<{
    (e: 'input', value: Array<ILayoutParametersList>): void
}>()

function isSizeOnlyGuard (settings: PropsSettingType): settings is PropsSizeOnlySettingType {
    return !!settings.sizeOnly
}

const isSizeOnly = computed(() => {
    return props.settings.sizeOnly
})

const fontSizeModel = computed({
    get () {
        const _value = props.value.find(item => item.LayoutParameterName === props.settings.fontParameterName)?.Value || props.settings.defaultSize
        return Number(_value)
    },
    set (value) {
        const parameter = props.value.find(item => item.LayoutParameterName === props.settings.fontParameterName)

        if (parameter) {
            parameter.Value = `${value}`
        }

        emit('input', props.value)
    }
})

const colorFontModel = computed({
    get () {
        const settings = props.settings

        if (settings.sizeOnly) {
            return ''
        }

        return props.value.find(item => item.LayoutParameterName === settings.colorParameterName)?.Value || settings.defaultColor
    },
    set (value) {
        const settings = props.settings

        if (isSizeOnlyGuard(settings)) {
            return
        }

        const nextValue = [ ...props.value ]
        const parameter = nextValue.find(item => item.LayoutParameterName === settings.colorParameterName)

        if (parameter) {
            parameter.Value = value
        } else {
            const defaultParameter = LAYOUT_PARAMETERS_DEFAULTS[settings.colorParameterName]

            if (defaultParameter) {
                nextValue.push({
                    ...defaultParameter,
                    Value: value
                })
            }
        }

        emit('input', nextValue)
    }
})

const sliderMarks = {
    8: '8',
    16: '16',
    24: '24',
    32: '32',
    40: '40',
    48: '48',
    56: '56'
}

</script>

<style lang="scss">
.layout-font-settings {
    direction: ltr;
    .el-slider__input {
        width: 68px;
    }
    .el-slider__runway.show-input {
        margin-right: 100px;
    }
    .el-slider__stop {
        background-color: transparent;
    }
}
</style>
